<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  lead: { type: Object, required: true },
});

defineEmits(['click']);

const { t } = useI18n();

const priorityBadge = computed(() => {
  const qs = props.lead.qualification_status;
  const p = props.lead.priority;
  if (qs === 'booked')
    return {
      label: 'Booked',
      cls: 'bg-emerald-100 text-emerald-700',
      dot: 'bg-emerald-500',
    };
  if (qs === 'lost')
    return {
      label: 'Lost',
      cls: 'bg-gray-100 text-gray-500',
      dot: 'bg-gray-400',
    };
  if (p === 'urgent' || p === 'high')
    return { label: 'Hot', cls: 'bg-red-100 text-red-600', dot: 'bg-red-500' };
  if (p === 'normal')
    return {
      label: 'Warm',
      cls: 'bg-amber-100 text-amber-700',
      dot: 'bg-amber-500',
    };
  return {
    label: 'Cold',
    cls: 'bg-blue-100 text-blue-600',
    dot: 'bg-blue-400',
  };
});

const channelBadge = computed(() => {
  const map = {
    whatsapp: { icon: 'i-lucide-message-circle', bg: 'bg-green-500' },
    email: { icon: 'i-lucide-mail', bg: 'bg-blue-500' },
    facebook: { icon: 'i-lucide-facebook', bg: 'bg-blue-700' },
    instagram: { icon: 'i-lucide-instagram', bg: 'bg-pink-500' },
    web_widget: { icon: 'i-lucide-globe', bg: 'bg-indigo-500' },
    api: { icon: 'i-lucide-code-2', bg: 'bg-gray-500' },
  };
  return (
    map[props.lead.source_channel] || {
      icon: 'i-lucide-help-circle',
      bg: 'bg-gray-400',
    }
  );
});

const timeDisplay = computed(() => {
  const raw = props.lead.created_at;
  if (!raw) return null;
  const d = new Date(raw);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString())
    return d.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
});

const requirementLine = computed(() => {
  const parts = [];
  if (props.lead.bedrooms) parts.push(props.lead.bedrooms);
  if (props.lead.preferred_location) parts.push(props.lead.preferred_location);
  if (props.lead.budget_max || props.lead.budget_min) {
    const sym = props.lead.currency === 'INR' ? '₹' : props.lead.currency || '';
    const fmt = v => {
      if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
      if (v >= 100000) return `${(v / 100000).toFixed(0)}L`;
      return Number(v).toLocaleString();
    };
    parts.push(`${sym}${fmt(props.lead.budget_max || props.lead.budget_min)}`);
  }
  return parts.join(' • ') || null;
});

const AGENT_COLORS = [
  'bg-indigo-100 text-indigo-700',
  'bg-purple-100 text-purple-700',
  'bg-blue-100 text-blue-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
];
const agentColor = computed(() => {
  const name = props.lead.assigned_agent?.name || '';
  return AGENT_COLORS[(name.charCodeAt(0) || 0) % AGENT_COLORS.length];
});
const agentInitials = computed(() => {
  const name = props.lead.assigned_agent?.name || '';
  return (
    name
      .split(' ')
      .map(n => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  );
});
const agentName = computed(() => props.lead.assigned_agent?.name || null);

const siteVisitChip = computed(() => {
  const raw = props.lead.next_site_visit_at;
  if (!raw) return null;
  const d = new Date(raw);
  const now = new Date();
  const tom = new Date(now);
  tom.setDate(now.getDate() + 1);
  const time = d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const fullDate =
    d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
    }) +
    ', ' +
    time;
  let label;
  if (d.toDateString() === now.toDateString()) {
    label = `Today ${time}`;
  } else if (d.toDateString() === tom.toDateString()) {
    label = `Tomorrow ${time}`;
  } else {
    label =
      d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) +
      ' ' +
      time;
  }
  const isVideo = props.lead.next_site_visit_type === 'video_call';
  const typeLabel = isVideo ? 'Video' : null;
  return { label, title: `Site visit: ${fullDate}`, isVideo, typeLabel };
});

const bookedOn = computed(() => {
  if (props.lead.qualification_status !== 'booked') return null;
  const raw = props.lead.booked_at || props.lead.updated_at;
  if (!raw) return null;
  return new Date(raw).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  });
});

const lostReason = computed(() =>
  props.lead.qualification_status === 'lost'
    ? props.lead.metadata?.lost_reason || null
    : null
);

const isBroker = computed(
  () => props.lead.lead_type === 'broker' || props.lead.broker_detected
);

const rejectionBadge = computed(() => {
  if (!props.lead.rejected_or_junk) return null;
  const label = props.lead.status_label === 'Junk' ? 'Junk' : 'Rejected';
  return { label, reason: props.lead.rejection_reason || null };
});

const scoreBarWidth = computed(
  () => `${Math.min(props.lead.lead_score ?? 0, 100)}%`
);
const scoreBarClass = computed(() => {
  const s = props.lead.lead_score ?? 0;
  if (s >= 80) return 'bg-green-500';
  if (s >= 60) return 'bg-amber-500';
  if (s >= 30) return 'bg-blue-400';
  return 'bg-n-slate-4';
});

const followUpChip = computed(() => {
  if (siteVisitChip.value || !props.lead.next_follow_up_at) return null;
  const d = new Date(props.lead.next_follow_up_at);
  const now = new Date();
  const tom = new Date(now);
  tom.setDate(now.getDate() + 1);
  const time = d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const fullDate =
    d.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
    }) +
    ', ' +
    time;
  if (d.toDateString() === now.toDateString())
    return { label: time, urgent: true, title: `Follow-up: ${fullDate}` };
  if (d.toDateString() === tom.toDateString())
    return {
      label: `Tomorrow ${time}`,
      urgent: false,
      title: `Follow-up: ${fullDate}`,
    };
  return {
    label: d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
    urgent: false,
    title: `Follow-up: ${fullDate}`,
  };
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div
    :data-lead-id="lead.id"
    class="bg-white dark:bg-n-solid-3 rounded-lg border shadow-xs hover:shadow-sm transition-all select-none"
    :class="
      rejectionBadge
        ? 'border-red-300 dark:border-red-500/40 hover:border-red-400 dark:hover:border-red-400'
        : 'border-n-weak dark:border-n-strong hover:border-woot-300 dark:hover:border-woot-600'
    "
    :title="lead.name || 'Unnamed Lead'"
  >
    <!-- Drag handle strip -->
    <div
      class="drag-handle flex items-center justify-center h-4 w-full rounded-t-lg cursor-grab active:cursor-grabbing bg-transparent hover:bg-n-alpha-2 transition-colors group"
      title="Drag to move"
    >
      <span
        class="i-lucide-grip-horizontal size-3 text-n-slate-5 group-hover:text-n-slate-7 transition-colors"
      />
    </div>

    <!-- Rejection / Junk banner -->
    <div
      v-if="rejectionBadge"
      class="mx-3 mb-1 flex items-center gap-1.5 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 px-2 py-1"
      :title="
        rejectionBadge.reason
          ? `Reason: ${rejectionBadge.reason}`
          : rejectionBadge.label
      "
    >
      <span
        class="i-lucide-ban size-3 text-red-600 dark:text-red-400 shrink-0"
      />
      <span class="text-xs font-semibold text-red-700 dark:text-red-400">
        {{ rejectionBadge.label }}
      </span>
      <span
        v-if="rejectionBadge.reason"
        class="text-[10px] text-red-500 dark:text-red-400/70 truncate"
      >
        · {{ rejectionBadge.reason }}
      </span>
    </div>

    <!-- Card body — click to open drawer -->
    <div
      class="px-3 pb-3 space-y-2 cursor-pointer"
      @click.stop="$emit('click', lead)"
    >
      <!-- Row 1: Priority badge · broker badge · time · channel icon -->
      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-1.5 min-w-0">
          <span
            :class="priorityBadge.cls"
            class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold shrink-0"
          >
            <span :class="priorityBadge.dot" class="w-1.5 h-1.5 rounded-full" />
            {{ priorityBadge.label }}
          </span>
          <span
            v-if="isBroker"
            class="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 shrink-0"
          >
            <span class="i-lucide-briefcase size-2.5" />
            Broker
          </span>
        </div>
        <div class="flex items-center gap-1.5 shrink-0">
          <span v-if="timeDisplay" class="text-[11px] text-n-slate-7">{{
            timeDisplay
          }}</span>
          <div
            :class="channelBadge.bg"
            class="w-5 h-5 rounded-full flex items-center justify-center"
            :title="lead.source_channel"
          >
            <span :class="channelBadge.icon" class="size-3 text-white" />
          </div>
        </div>
      </div>

      <!-- Name -->
      <p
        class="font-semibold text-sm text-n-slate-12 dark:text-n-slate-11 leading-snug"
      >
        {{ lead.name || 'Unnamed Lead' }}
      </p>

      <!-- Project name chip -->
      <span
        v-if="lead.project?.name"
        class="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-woot-50 text-woot-700 dark:bg-woot-500/10 dark:text-woot-400 font-medium -mt-1"
      >
        <span class="i-lucide-building-2 size-2.5 shrink-0" />
        {{ lead.project.name }}
      </span>

      <!-- Requirement: BHK • Location • Budget -->
      <p v-if="requirementLine" class="text-xs text-n-slate-9 leading-snug">
        {{ requirementLine }}
      </p>

      <!-- Phone -->
      <p v-if="lead.phone_number" class="text-xs text-n-slate-10">
        {{ lead.phone_number }}
      </p>

      <!-- Booked on -->
      <p v-if="bookedOn" class="text-xs text-emerald-600 font-medium">
        {{ t('NEXCEO.KANBAN_CRM.CARD.BOOKED_ON') }} {{ bookedOn }}
      </p>

      <!-- Lost reason -->
      <p v-if="lostReason" class="text-xs text-n-slate-7 italic">
        {{ lostReason }}
      </p>

      <!-- Site visit chip -->
      <div
        v-if="siteVisitChip"
        class="inline-flex items-center gap-1 text-xs border px-2 py-0.5 rounded-full"
        :class="
          siteVisitChip.isVideo
            ? 'bg-woot-50 text-woot-700 border-woot-200'
            : 'bg-green-50 text-green-700 border-green-200'
        "
        :title="siteVisitChip.title"
      >
        <span
          :class="
            siteVisitChip.isVideo
              ? 'i-lucide-video size-3'
              : 'i-lucide-calendar-check size-3'
          "
        />
        {{ siteVisitChip.label }}
        <span v-if="siteVisitChip.typeLabel" class="font-semibold">
          · {{ siteVisitChip.typeLabel }}
        </span>
      </div>

      <!-- Follow-up chip -->
      <div
        v-else-if="followUpChip"
        class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border"
        :class="
          followUpChip.urgent
            ? 'bg-red-50 text-red-600 border-red-200'
            : 'bg-amber-50 text-amber-700 border-amber-200'
        "
        :title="followUpChip.title"
      >
        <span class="i-lucide-clock size-3" />
        {{ followUpChip.label }}
      </div>

      <!-- Agent -->
      <div v-if="agentName" class="flex items-center gap-1.5 pt-0.5">
        <div
          :class="agentColor"
          class="w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center shrink-0"
        >
          {{ agentInitials }}
        </div>
        <span class="text-xs text-n-slate-9 truncate">{{ agentName }}</span>
      </div>

      <!-- AI score bar -->
      <div
        v-if="lead.lead_score != null"
        class="flex items-center gap-1.5 pt-0.5"
        :title="`AI Score: ${lead.lead_score}/100`"
      >
        <div class="flex-1 h-1 rounded-full bg-n-alpha-3 overflow-hidden">
          <div
            class="h-full rounded-full transition-all"
            :class="scoreBarClass"
            :style="{ width: scoreBarWidth }"
          />
        </div>
        <span class="text-[10px] text-n-slate-7 tabular-nums shrink-0">{{
          lead.lead_score
        }}</span>
      </div>
    </div>
  </div>
</template>
